import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import type { Position } from '@sorare/core/src/__generated__/globalTypes';
import { SeasonEligibility } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { DisplayS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { isType } from '@sorare/core/src/lib/gql';
import { positionShortNames } from '@sorare/core/src/lib/players';

import { PriceHistoryFromProps } from '@sorare/marketplace/src/components/price/PriceHistoryFromProps';

import type { LastSales_anyCard } from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ gap: 3 })`
  --player-picture-url: url('');

  border-radius: var(--double-unit);
  padding: var(--triple-unit) var(--double-unit);
  pointer-events: none;
  height: 100%;

  background:
    linear-gradient(
      to top,
      var(--c-black) calc(100% - 96px),
      transparent calc(100% - 50px)
    ),
    no-repeat calc(100% - var(--unit)) var(--triple-unit) / 96px auto
      var(--player-picture-url),
    var(--c-black);

  & > * {
    width: 100%;
  }
`;
const LeftAlignedVertical = styled(Vertical)`
  align-items: flex-start;
`;

type Props = { card: LastSales_anyCard };
export const LastSales = ({ card }: Props) => {
  const pictureUrl = isType(card.anyPlayer, 'Player')
    ? card.anyPlayer.pictureUrl
    : card.anyPlayer.avatarPictureUrl;

  return (
    <Root
      key={card.slug}
      style={
        {
          '--player-picture-url': `url(${pictureUrl})`,
        } as CSSProperties
      }
    >
      <Vertical gap={2}>
        <LeftAlignedVertical gap={0}>
          <DisplayS bold>{card.anyPlayer.firstName}</DisplayS>
          <DisplayS bold>{card.anyPlayer.lastName}</DisplayS>
        </LeftAlignedVertical>
        <Horizontal>
          <Chip>
            <FormattedMessage
              {...positionShortNames[card.anyPositions[0] as Position]}
            />
          </Chip>
          {!!card.anyPlayer.activeClub?.pictureUrl && (
            <Chip>
              <Horizontal gap={0.5}>
                <img
                  height={12}
                  src={card.anyPlayer.activeClub.pictureUrl}
                  alt={card.anyPlayer.activeClub.slug}
                />
                {card.anyPlayer.activeClub.code}
              </Horizontal>
            </Chip>
          )}
        </Horizontal>
      </Vertical>
      <PriceHistoryFromProps
        variables={{
          playerSlug: card.anyPlayer.slug,
          rarity: card.rarityTyped,
          seasonEligibility: card.inSeasonEligible
            ? SeasonEligibility.IN_SEASON
            : SeasonEligibility.CLASSIC,
        }}
        count={3}
        inBlock={false}
        showTokenOfferSender
      />
    </Root>
  );
};

LastSales.fragments = {
  anyCard: gql`
    fragment LastSales_anyCard on AnyCardInterface {
      slug
      rarityTyped
      inSeasonEligible
      anyPositions
      anyPlayer {
        slug
        avatarPictureUrl
        ... on Player {
          slug
          pictureUrl
        }
        firstName
        lastName
        activeClub {
          slug
          code
          pictureUrl
        }
      }
    }
  ` as TypedDocumentNode<LastSales_anyCard>,
};
